var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Matrix4, Vec3, Vec4 } from 'ayvri-std/lib/vector';
import { geodeticSurfaceNormal } from 'ayvri-std/lib/ellipsoid';
import { defined, defaultValue, buildAssetUrl } from './helpers';
const kMarkerModelUri = 'assets/models/DistanceMarker.glb';
const kTextureWidth = 512;
const kTextureHeight = 256;
const kModelScale = 100.0;
class Marker {
    constructor() {
        this.ready = false;
        this.activated = false;
    }
    load(globe, pos, desc, headless) {
        return __awaiter(this, void 0, void 0, function* () {
            this.pos = pos;
            this.desc = desc;
            this.distance = desc.distance;
            if (headless) {
                this.ready = true;
                return;
            }
            this.transform = new Matrix4();
            this.transform.setColumn(3, pos);
            this.transform.m[15] = 1.0;
            this.model = globe.addModel({
                uri: buildAssetUrl(kMarkerModelUri),
                position: pos,
                scale: kModelScale,
            });
            // Generate the text image
            const canvas = document.createElement('canvas');
            canvas.width = kTextureWidth;
            canvas.height = kTextureHeight;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.fillStyle = '#000000';
            ctx.font = '180px Rubik Mono One, Arial Black, Arial, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText(desc.text, canvas.width / 2, canvas.height * 0.7);
            ctx.font = '50px Rubik Mono One, Arial Black, Arial, sans-serif';
            ctx.fillText(desc.subtext, canvas.width / 2, canvas.height / 2 + 100);
            this.model.setTexture(canvas).then(() => {
                this.ready = true;
            });
        });
    }
    activate() {
        this.activated = true;
        this.model.readyPromise.then(() => {
            this.model.startAnimation('Appear', false, 2.0);
            this.model.startAnimation('MorphAppear', false, 2.0);
        });
    }
    deactivate() {
        this.activated = false;
    }
    destroy(globe) {
        globe.deleteModel(this.model);
    }
}
/**
 * Generate Markers (a MediaItem) at specific distances along a given track.
 * Markers will be re-generated upon switching of units (e.g. km vs. miles)
 */
export default class DistanceMarkers {
    constructor(headless) {
        this.markers = [];
        this.headless = headless;
    }
    onSceneLoad(globe, scene) {
        this.globe = globe;
        if (defined(scene.distanceMarkers)) {
            this.units = undefined;
            this.settings = scene.distanceMarkers;
            this.settings.frequency = defaultValue(this.settings.frequency, 5);
            this.settings.color = defaultValue(this.settings.color, 'rgb(244, 238, 66)');
            this.settings.height = defaultValue(this.settings.height, 200);
            this.trackId = scene.course ? '__courseActivity' : Object.keys(scene.activities)[0];
        }
    }
    update(camera, tracks, state) {
        if (!this.trackId) {
            return;
        }
        if (!this.headless) {
            // Update billboard matrices for each distance marker model
            for (let i = 0; i < this.markers.length; i++) {
                const model = this.markers[i].model;
                const pos = this.markers[i].pos;
                const transform = this.markers[i].transform;
                const up = geodeticSurfaceNormal(pos);
                const forward = Vec3.subtract(camera.pos, pos, new Vec3()).normalize();
                const right = Vec3.cross(up, forward, new Vec3());
                Vec3.cross(right, up, forward);
                transform.setColumn(0, Vec4.fromVec3(Vec3.multiply(right, kModelScale, right), 0.0));
                transform.setColumn(1, Vec4.fromVec3(Vec3.multiply(up, kModelScale, up), 0.0));
                transform.setColumn(2, Vec4.fromVec3(Vec3.multiply(forward, kModelScale, forward), 0.0));
                model.setTransform(transform);
            }
            // Trigger appear animations if the avatar passes by a marker
            const playhead = state.playheads[state.tracks.activeTrack];
            const curDistance = state.course ? playhead.courseDistance : playhead.cumulative;
            for (let i = 0; i < this.markers.length; i++) {
                const marker = this.markers[i];
                if (curDistance > marker.distance && !marker.activated)
                    marker.activate();
                else if (curDistance < marker.distance && marker.activated)
                    marker.deactivate();
            }
        }
        // If the user changes units, reinitialize all of the markers
        // @NOTE: This will also trigger a call to generateMarkers on first update
        if (this.units !== state.stats.units) {
            this.units = state.stats.units;
            const track = tracks.getTrack(this.trackId);
            this.deleteMarkers();
            this.generateMarkersForTrack(track, this.units);
        }
    }
    onPointsAdded(tracks) {
        if (!this.trackId) {
            return;
        }
        const track = tracks.getTrack(this.trackId);
        // If the track got new points, just regenerate all the markers
        // @TODO: Only handle new points
        if (track.dirty) {
            this.deleteMarkers();
            this.generateMarkersForTrack(track, this.units);
        }
    }
    deleteMarkers() {
        if (!this.headless) {
            this.markers.forEach(m => {
                m.destroy(this.globe);
            });
        }
        this.markers = [];
    }
    generateMarkersForTrack(track, units) {
        // Find the marker positions along the track based on the specified frequency
        const unitsScale = units === 'metric' ? 1 : 0.621371;
        const interval = (this.settings.frequency * 1000) / unitsScale;
        const result = { x: 0.0, y: 0.0, z: 0.0 };
        const points = track.sampleInterval({ cumulative: interval }, result);
        // Create a marker for each point
        for (let i = 0; i < points.length; i++) {
            const pos = points[i];
            if (pos) {
                const marker = new Marker();
                const distanceInUnits = i * this.settings.frequency;
                const desc = {
                    color: this.settings.color,
                    distance: i * interval,
                    text: distanceInUnits.toString(),
                    subtext: units === 'metric' ? 'km' : 'mi',
                };
                marker.load(this.globe, pos, desc, this.headless);
                this.markers.push(marker);
            }
        }
    }
}
